import React, { useState, useEffect, useContext } from "react";
import openSocket from "socket.io-client";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Select from "@material-ui/core/Select";
import { toast } from "react-toastify";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Switch from "@material-ui/core/Switch";
import { AuthContext } from "../../context/Auth/AuthContext";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		alignItems: "center",
		padding: theme.spacing(8, 8, 3),
	},
	
	paper: {
		padding: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		marginBottom: 12,
		
	},
	
	settingOption: {
		marginLeft: "auto",
	},
	margin: {
		margin: theme.spacing(1),
	},
	
	workingHoursSection: {
		marginBottom: theme.spacing(3),
	},
	
	workingHoursTitle: {
		marginBottom: theme.spacing(2),
		fontWeight: 600,
		color: theme.palette.primary.main,
	},
	
	workingHoursCard: {
		padding: theme.spacing(3),
		marginBottom: theme.spacing(2),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.spacing(1),
		backgroundColor: theme.palette.background.paper,
	},
	
	periodTitle: {
		fontWeight: 500,
		marginBottom: theme.spacing(2),
		color: theme.palette.text.primary,
	},
	
	timeFieldsContainer: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(2),
		flexWrap: "wrap",
	},
	
	timeField: {
		minWidth: "120px",
		flex: "1 1 auto",
	},
	
	timeSeparator: {
		fontSize: "1.2rem",
		fontWeight: 500,
		color: theme.palette.text.secondary,
		margin: theme.spacing(0, 1),
	},
	
	workingHoursHeader: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: theme.spacing(2),
	},
	
	resetButton: {
		marginLeft: theme.spacing(1),
	},
	
	saveIndicator: {
		fontSize: "0.75rem",
		color: theme.palette.success.main,
		fontStyle: "italic",
	},
	
	savingIndicator: {
		fontSize: "0.75rem",
		color: theme.palette.warning.main,
		fontStyle: "italic",
	},
	
	saveButton: {
		marginLeft: theme.spacing(1),
		backgroundColor: theme.palette.success.main,
		color: theme.palette.common.white,
		"&:hover": {
			backgroundColor: theme.palette.success.dark,
		},
	},
	
	unsavedIndicator: {
		fontSize: "0.75rem",
		color: theme.palette.warning.main,
		fontStyle: "italic",
	},
	

	
}));

const Settings = () => {
	const classes = useStyles();
	
	const [settings, setSettings] = useState([]);
	const [workingHours, setWorkingHours] = useState(null);
	const [isSaving, setIsSaving] = useState(false);
	const [lastSaved, setLastSaved] = useState(null);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
	
	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, []);

	useEffect(() => {
		const fetchWorkingHours = async () => {
			try {
				const { data } = await api.get("/working-hours");
				setWorkingHours(data);
			} catch (err) {
				// Se não existir horário configurado, não é erro
				if (err.response?.status !== 404) {
					toastError(err);
				}
			}
		};
		fetchWorkingHours();
	}, []);

	useEffect(() => {
		const socket = openSocket(process.env.REACT_APP_BACKEND_URL);

		socket.on("settings", data => {
			if (data.action === "update") {
				setSettings(prevState => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex(s => s.key === data.setting.key);
					aux[settingIndex].value = data.setting.value;
					return aux;
				});
			}
		});

		socket.on("workingHours", data => {
			if (data.action === "update" || data.action === "create") {
				setWorkingHours(data.workingHours);
			}
		});

		return () => {
			socket.disconnect();
		};
	}, []);



	const handleChangeSetting = async e => {
		const selectedValue = e.target.value;
		const settingKey = e.target.name;

		try {
			await api.put(`/settings/${settingKey}`, {
				value: selectedValue,
			});
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

		const handleChangeWorkingHours = (field, value) => {
		// Atualiza apenas o estado local para feedback visual
		setWorkingHours(prev => ({
			...prev,
			[field]: value
		}));
		
		// Marca que há mudanças não salvas
		setHasUnsavedChanges(true);
	};

	const saveWorkingHours = async () => {
		setIsSaving(true);
		try {
			const currentWorkingHours = workingHours;
			const currentId = currentWorkingHours?.id;
			
			const baseHours = {
				weekdaysStart: "09:00",
				weekdaysEnd: "18:00",
				saturdayStart: "09:00",
				saturdayEnd: "13:00"
			};

			const updatedHours = {
				...baseHours,
				...(currentWorkingHours || {})
			};

			if (currentId) {
				await api.put(`/working-hours/${currentId}`, updatedHours);
			} else {
				const response = await api.post("/working-hours", updatedHours);
				if (response.data?.id) {
					setWorkingHours(prev => ({ ...prev, id: response.data.id }));
				}
			}
			
			setLastSaved(new Date());
			setHasUnsavedChanges(false);
			toast.success("Horário de expediente salvo com sucesso!");
		} catch (err) {
			toastError(err);
		} finally {
			setIsSaving(false);
		}
	};

	const handleResetWorkingHours = async () => {
		const defaultHours = {
			weekdaysStart: "09:00",
			weekdaysEnd: "18:00",
			saturdayStart: "09:00",
			saturdayEnd: "13:00"
		};
		
		// Atualiza o estado local imediatamente
		setWorkingHours(prev => prev ? { ...prev, ...defaultHours } : defaultHours);
		
		// Salva imediatamente no backend
		setIsSaving(true);
		try {
			if (workingHours?.id) {
				await api.put(`/working-hours/${workingHours.id}`, defaultHours);
			} else {
				const response = await api.post("/working-hours", defaultHours);
				if (response.data?.id) {
					setWorkingHours(prev => ({ ...prev, id: response.data.id }));
				}
			}
			setLastSaved(new Date());
			setHasUnsavedChanges(false);
			toast.success("Horários restaurados para o padrão!");
		} catch (err) {
			toastError(err);
		} finally {
			setIsSaving(false);
		}
	};

	const getSettingValue = key => {
		const { value } = settings.find(s => s.key === key);
		return value;
	};

	return (
		<div className={classes.root}>
			<Container className={classes.container} maxWidth="sm">
				<Typography variant="body2" gutterBottom>
					{i18n.t("settings.title")}
				</Typography>
				
				<div className={classes.workingHoursSection}>
					<div className={classes.workingHoursHeader}>
						<Typography variant="h6" className={classes.workingHoursTitle}>
							🕒 Horário de Expediente
						</Typography>
						<div style={{ display: "flex", alignItems: "center" }}>
							{isSaving && (
								<Typography className={classes.savingIndicator}>
									Salvando...
								</Typography>
							)}
							{!isSaving && !hasUnsavedChanges && lastSaved && (
								<Typography className={classes.saveIndicator}>
									Salvo às {lastSaved.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
								</Typography>
							)}
							<Tooltip title="Salvar mudanças">
								<Button
									variant="contained"
									size="small"
									onClick={saveWorkingHours}
									className={classes.saveButton}
									disabled={isSaving || !hasUnsavedChanges}
								>
									💾 Salvar
								</Button>
							</Tooltip>
							<Tooltip title="Restaurar horários padrão">
								<Button
									variant="outlined"
									size="small"
									onClick={handleResetWorkingHours}
									className={classes.resetButton}
									disabled={isSaving}
								>
									🔄 Padrão
								</Button>
							</Tooltip>
						</div>
					</div>
					
					<div className={classes.workingHoursCard}>
						<Typography variant="body1" style={{ fontWeight: 500, color: '#3f51b5' }}>
							📅 Segunda à Sexta
						</Typography>
						<Typography variant="caption" style={{ color: '#666', marginTop: '5px' }}>
							Deixe 00:00 no inicio e fim caso queira a mensagem personalizada de feriado.
						</Typography>
						<div className={classes.timeFieldsContainer} style={{ marginTop: '8px' }}>
							<TextField
								margin="dense"
								variant="outlined"
								type="time"
								id="weekdaysStart-setting"
								label="Início"
								value={workingHours?.weekdaysStart || "09:00"}
								onChange={(e) => handleChangeWorkingHours('weekdaysStart', e.target.value)}
								InputLabelProps={{
									shrink: true,
								}}
								className={classes.timeField}
								size="small"
							/>
							<Typography className={classes.timeSeparator}>até</Typography>
							<TextField
								margin="dense"
								variant="outlined"
								type="time"
								id="weekdaysEnd-setting"
								label="Fim"
								value={workingHours?.weekdaysEnd || "18:00"}
								onChange={(e) => handleChangeWorkingHours('weekdaysEnd', e.target.value)}
								InputLabelProps={{
									shrink: true,
								}}
								className={classes.timeField}
								size="small"
							/>
						</div>
					</div>
					
					<div className={classes.workingHoursCard}>
						<Typography variant="body1" style={{ fontWeight: 500, color: '#3f51b5' }}>
							📅 Sábados
						</Typography>
						<div className={classes.timeFieldsContainer} style={{ marginTop: '8px' }}>
							<TextField
								margin="dense"
								variant="outlined"
								type="time"
								id="saturdayStart-setting"
								label="Início"
								value={workingHours?.saturdayStart || "09:00"}
								onChange={(e) => handleChangeWorkingHours('saturdayStart', e.target.value)}
								InputLabelProps={{
									shrink: true,
								}}
								className={classes.timeField}
								size="small"
							/>
							<Typography className={classes.timeSeparator}>até</Typography>
							<TextField
								margin="dense"
								variant="outlined"
								type="time"
								id="saturdayEnd-setting"
								label="Fim"
								value={workingHours?.saturdayEnd || "13:00"}
								onChange={(e) => handleChangeWorkingHours('saturdayEnd', e.target.value)}
								InputLabelProps={{
									shrink: true,
								}}
								className={classes.timeField}
								size="small"
							/>
						</div>
					</div>
				</div>

				<Paper className={classes.paper} style={{ borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
					<div style={{ display: 'flex', flexDirection: 'column', width: '70%' }}>
						<Typography variant="body1" style={{ fontWeight: 500, color: '#3f51b5' }}>
							Validar acesso do usuário à instância
						</Typography>
						<Typography variant="caption" style={{ color: '#666', marginTop: '4px' }}>
							Se desativado, valida apenas se o usuário está ativo no LAMP, ignorando o acesso à instância.
						</Typography>
					</div>
					<div className={classes.settingOption}>
						<Switch
							checked={
								settings && settings.length > 0 && getSettingValue("regraAcessoInstancia") === "true"
							}
							onChange={e => handleChangeSetting({
								target: {
									name: "regraAcessoInstancia",
									value: e.target.checked ? "true" : "false"
								}
							})}
							name="regraAcessoInstancia"
							inputProps={{ "aria-label": "Validação de acesso à instância" }}
						/>
					</div>
				</Paper>
				<Paper className={classes.paper}>
					<Typography variant="body1">
						Tipo Lampzap
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						id="tipoLampzap-setting"
						name="tipoLampzap"
						value={
							settings && settings.length > 0 && getSettingValue("tipoLampzap")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="loja">
							Loja 
						</option>
						<option value="site">
							Site
						</option>
					</Select>
				</Paper>
				<Paper className={classes.paper}>
					<Typography variant="body1">
						Tipo Distribuição
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						id="sistemaDistribuicao-setting"
						name="sistemaDistribuicao"
						value={
							settings && settings.length > 0 && getSettingValue("sistemaDistribuicao")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="enabled">
							Com distribuição (Um por Um) 
						</option>
						<option value="disabled">
							Sem distribuição
						</option>
					</Select>
				</Paper>
				<Paper className={classes.paper}>
				<Typography variant="body1">
					Qntd. Dias Distribuição
				</Typography>
				<TextField
					margin="dense"
					variant="outlined"
					id="sistemaDistribuicaoDias-setting"
					name="sistemaDistribuicaoDias"
					value={
					settings && settings.length > 0 && getSettingValue("sistemaDistribuicaoDias")
					}
					className={classes.settingOption}
					onChange={handleChangeSetting}
				/>
				</Paper>
				<Paper className={classes.paper}>
					<Typography variant="body1">
						{i18n.t("settings.settings.userCreation.name")}
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						id="userCreation-setting"
						name="userCreation"
						value={
							settings && settings.length > 0 && getSettingValue("userCreation")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="enabled">
							{i18n.t("settings.settings.userCreation.options.enabled")}
						</option>
						<option value="disabled">
							{i18n.t("settings.settings.userCreation.options.disabled")}
						</option>
					</Select>
				</Paper>
							
				<Paper className={classes.paper}>
					<Typography variant="body1">
						Tipo Linha
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						id="tipoLinha-setting"
						name="tipoLinha"
						value={
							settings && settings.length > 0 && getSettingValue("tipoLinha")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="enabled">
							Linha Atribuida
						</option>
						<option value="disabled">
							Linha Aleatória
						</option>
					</Select>
				</Paper>	
				<Paper className={classes.paper}>
					<Typography variant="body1">
						Bloq. Atribuição 30s
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						id="cooldown-setting"
						name="cooldown"
						value={
							settings && settings.length > 0 && getSettingValue("cooldown")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="enabled">
							Ativado
						</option>
						<option value="disabled">
							Desativado
						</option>
					</Select>
				</Paper>
				<Paper className={classes.paper}>
					<Typography variant="body1">
						Ordenação de tickets:
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						id="tipoOrdenacao-setting"
						name="tipoOrdenacao"
						value={
							settings && settings.length > 0 && getSettingValue("tipoOrdenacao")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="0">
							Antigo
						</option>
						<option value="1">
							Novo
						</option>
					</Select>
				</Paper>
				<Paper className={classes.paper}>
					<Typography variant="body1">
						Limite de etiquetas
					</Typography>
					<TextField
						margin="dense"
						variant="outlined"
						id="tagLimit-setting"
						name="tagLimit"
						value={
						settings && settings.length > 0 && getSettingValue("tagLimit")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					/>
				</Paper>
				<Paper className={classes.paper}>
					<Typography variant="body1">
						Limite de tabulação
					</Typography>
					<TextField
						margin="dense"
						variant="outlined"
						id="reasonLimit-setting"
						name="reasonLimit"
						value={
						settings && settings.length > 0 && getSettingValue("reasonLimit")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					/>
				</Paper>
				<Paper className={classes.paper}>
					<Typography variant="body1">
						Limpar etiquetas fechadas (dias)
					</Typography>
					<TextField
						margin="dense"
						variant="outlined"
						id="tagClearDays-setting"
						name="tagClearDays"
						value={
						settings && settings.length > 0 && getSettingValue("tagClearDays")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					/>
				</Paper>				
				<Paper className={classes.paper}>
					<Typography variant="body1">
						Transferir Tickets
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						id="ticketTransfer-setting"
						name="ticketTransfer"
						value={
							settings && settings.length > 0 && getSettingValue("ticketTransfer")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="enabled">
							Ativado
						</option>
						<option value="disabled">
							Desativado
						</option>
					</Select>
				</Paper>
				<Paper className={classes.paper} style={{ borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
					<div style={{ display: 'flex', flexDirection: 'column', width: '70%' }}>
						<Typography variant="body1" style={{ fontWeight: 500, color: '#3f51b5' }}>
							Visualizar tickets alheios
						</Typography>
						<Typography variant="caption" style={{ color: '#666', marginTop: '4px' }}>
							Permite que o usuário vendedor acesse qualquer ticket através da URL.
						</Typography>
					</div>
					<div className={classes.settingOption}>
						<Switch
							checked={
								settings && settings.length > 0 && getSettingValue("visualizarTicketAlheio") === "enabled"
							}
							onChange={e => handleChangeSetting({
								target: {
									name: "visualizarTicketAlheio",
									value: e.target.checked ? "enabled" : "disabled"
								}
							})}
							name="visualizarTicketAlheio"
							inputProps={{ "aria-label": "Visualizar Tickets Alheios" }}
						/>
					</div>
				</Paper>
				<Paper className={classes.paper} style={{ borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
					<div style={{ display: 'flex', flexDirection: 'column', width: '70%' }}>
						<Typography variant="body1" style={{ fontWeight: 500, color: '#3f51b5' }}>
							Permitir reabrir tickets de outros usuários
						</Typography>
						<Typography variant="caption" style={{ color: '#666', marginTop: '4px' }}>
							Permite que um atendente reabra um ticket encerrado que foi atendido por outro usuário.
						</Typography>
					</div>
					<div className={classes.settingOption}>
						<Switch
							checked={
								settings && settings.length > 0 && getSettingValue("permitirReabrirTicketAlheio") === "enabled"
							}
							onChange={e => handleChangeSetting({
								target: {
									name: "permitirReabrirTicketAlheio",
									value: e.target.checked ? "enabled" : "disabled"
								}
							})}
							name="permitirReabrirTicketAlheio"
							inputProps={{ "aria-label": "Permitir Reabrir Tickets de Outros Usuários" }}
						/>
					</div>
				</Paper>
				<Paper className={classes.paper} style={{ borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
					<div style={{ display: 'flex', flexDirection: 'column', width: '70%' }}>
						<Typography variant="body1" style={{ fontWeight: 500, color: '#3f51b5' }}>
							Enviar Nome do Operador
						</Typography>
						<Typography variant="caption" style={{ color: '#666', marginTop: '4px' }}>
							Quando ativado, o nome do operador logado é enviado junto à mensagem para o cliente (ex.: Maria: Olá tudo bem?).
						</Typography>
					</div>
					<div className={classes.settingOption}>
						<Switch
							checked={
								settings && settings.length > 0 && getSettingValue("enviarAssinaturaOperador") === "enabled"
							}
							onChange={e => handleChangeSetting({
								target: {
									name: "enviarAssinaturaOperador",
									value: e.target.checked ? "enabled" : "disabled"
								}
							})}
							name="enviarAssinaturaOperador"
							inputProps={{ "aria-label": "Enviar Nome do Operador" }}
						/>
					</div>
				</Paper>
				<Paper className={classes.paper} style={{ borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
					<div style={{ display: 'flex', flexDirection: 'column', width: '70%' }}>
						<Typography variant="body1" style={{ fontWeight: 500, color: '#3f51b5' }}>
							Transcrição de Áudio
						</Typography>
						<Typography variant="caption" style={{ color: '#666', marginTop: '4px' }}>
							Quando ativado, áudios recebidos são transcritos automaticamente para texto.
						</Typography>
					</div>
					<div className={classes.settingOption}>
						<Switch
							checked={
								settings && settings.length > 0 && getSettingValue("transcricaoAudio") === "enabled"
							}
							onChange={e => handleChangeSetting({
								target: {
									name: "transcricaoAudio",
									value: e.target.checked ? "enabled" : "disabled"
								}
							})}
							name="transcricaoAudio"
							inputProps={{ "aria-label": "Transcrição de Áudio" }}
						/>
					</div>
				</Paper>
				<Typography variant="body2" gutterBottom>
					{"Limites de template"}
				</Typography>
				<Paper className={classes.paper}>
					<Typography variant="body1">
						Primeiro Contato
					</Typography>
					<TextField
						margin="dense"
						variant="outlined"
						id="templateLimitFirstContact-setting"
						name="templateLimitFirstContact"
						value={
							settings && settings.length > 0 && getSettingValue("templateLimitFirstContact")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					/>
				</Paper>
				
				<Paper className={classes.paper}>
					<Typography variant="body1">
						Retorno Contato
					</Typography>
					<TextField
						margin="dense"
						variant="outlined"
						id="templateLimitContactReturn-setting"
						name="templateLimitContactReturn"
						value={
							settings && settings.length > 0 && getSettingValue("templateLimitContactReturn")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					/>
				</Paper>
			</Container>
		</div>
	);
};

export default Settings;
